import SocialProofBanner from '@/components/SocialProofBanner'
import SuccessStats from '@/components/SuccessStats'
import TestimonialCarousel from '@/components/TestimonialCarousel'
import FrequentlyAskedQuestions from '@app-studio-web/ui/components/FrequentlyAskedQuestions'
import StarRating from '@app-studio-web/ui/components/StarRating'
import { useAppConfig } from '@app-studio-web/ui/contexts/AppConfigContext'
import { type ComponentType, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import {
  FeedbackMockup,
  FlowMockupStyles,
  SpeakingMockup,
  TutorMockup,
} from './FlowMockups'

const FLAG_EMOJIS = [
  '🇬🇧',
  '🇪🇸',
  '🇩🇪',
  '🇫🇷',
  '🇮🇹',
  '🇵🇹',
  '🇳🇱',
  '🇯🇵',
  '🇨🇳',
  '🇰🇷',
  '🇷🇺',
  '🇮🇩',
  '🇵🇱',
  '🇩🇰',
  '🇳🇴',
  '🇸🇪',
  '🇹🇷',
]

const COMPARISON_ROWS = [
  'pages.flow.comparison.rows.row1',
  'pages.flow.comparison.rows.row2',
  'pages.flow.comparison.rows.row3',
  'pages.flow.comparison.rows.row4',
  'pages.flow.comparison.rows.row5',
]

const HERO_BULLETS = [
  'pages.flow.bullets.b1',
  'pages.flow.bullets.b2',
  'pages.flow.bullets.b3',
]

const RESEARCH_BARS = [
  { labelKey: 'pages.flow.research.chart.lectures', value: 5 },
  { labelKey: 'pages.flow.research.chart.reading', value: 10 },
  { labelKey: 'pages.flow.research.chart.traditionalApps', value: 25 },
  {
    labelKey: 'pages.flow.research.chart.immersion',
    value: 75,
    highlight: true,
  },
]

const HOW_STEPS: {
  titleKey: string
  textKey: string
  Mockup: ComponentType
}[] = [
  {
    titleKey: 'pages.flow.how.step1Title',
    textKey: 'pages.flow.how.step1Text',
    Mockup: SpeakingMockup,
  },
  {
    titleKey: 'pages.flow.how.step2Title',
    textKey: 'pages.flow.how.step2Text',
    Mockup: FeedbackMockup,
  },
  {
    titleKey: 'pages.flow.how.step3Title',
    textKey: 'pages.flow.how.step3Text',
    Mockup: TutorMockup,
  },
]

function CheckIcon() {
  return (
    <span className="bg-primary flex h-6 w-6 items-center justify-center rounded-full text-white">
      <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
        <path
          d="M5 10.5l3.5 3.5L15 6"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

function CrossIcon() {
  return (
    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#e2e8f0] text-[#94a3b8]">
      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
        <path
          d="M6 6l8 8M14 6l-8 8"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
    </span>
  )
}

export default function FlowLandingPage({
  startPath = '/onboarding/language',
}: {
  startPath?: string
} = {}) {
  const { logo } = useAppConfig()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showStickyCta, setShowStickyCta] = useState(false)

  // The landing page owns the funnel entry URL, so send the CTA straight to the
  // first question. Defaults to the regular /onboarding funnel; standalone
  // funnels (e.g. /quiz) pass their own start path.
  const startOnboarding = () => navigate(startPath)

  useEffect(() => {
    const onScroll = () => setShowStickyCta(window.scrollY > 420)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="page-bg-gradient min-h-dvh">
      <FlowMockupStyles />

      <div className="bg-primary px-4 py-2 text-center text-sm font-bold text-white">
        {t('pages.flow.promo')}
      </div>

      {!('ReactNativeWebView' in window) && (
        <header className="mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-4">
          {logo}
          <button
            className="bg-primary cursor-pointer rounded-xl px-5 py-2.5 text-sm font-bold text-white transition-all hover:brightness-110 active:scale-[0.98]"
            onClick={startOnboarding}
          >
            {t('pages.flow.cta')}
          </button>
        </header>
      )}

      {/* Hero */}
      <section className="mx-auto flex max-w-3xl flex-col items-center px-6 pt-8 pb-14 text-center">
        <p className="mb-3 text-sm font-semibold text-[#4a5568]">
          {t('pages.flow.preHeadline')}
        </p>

        <div className="bg-primary/10 text-primary mb-5 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold">
          <span className="bg-primary inline-block h-1.5 w-1.5 animate-pulse rounded-full" />
          {t('pages.flow.badge')}
        </div>

        <h1
          className="text-[34px] leading-[1.08] font-extrabold tracking-tight text-[#0a1628] md:text-[52px]"
          data-testid="label-header"
        >
          {t('pages.flow.heading')}
        </h1>

        <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-[#4a5568] md:text-[20px]">
          {t('pages.flow.subheading')}
        </p>

        <ul className="mx-auto mt-6 flex w-fit max-w-md flex-col gap-3 text-left">
          {HERO_BULLETS.map((bulletKey) => (
            <li key={bulletKey} className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0">
                <CheckIcon />
              </span>
              <span className="text-[15px] font-medium text-[#0a1628]">
                {t(bulletKey)}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex items-center justify-center gap-2">
          <StarRating rating={4.7} />
          <span className="text-sm font-semibold text-[#0a1628]">
            4.7 · {t('pages.flow.ratingCount')}
          </span>
        </div>

        <button
          className="bg-primary shadow-primary/25 hover:shadow-primary/35 mt-8 inline-flex cursor-pointer items-center gap-2 rounded-2xl px-10 py-4 text-lg font-bold text-white uppercase shadow-lg transition-all hover:shadow-xl hover:brightness-110 active:scale-[0.98]"
          data-testid="button-continue"
          onClick={startOnboarding}
        >
          {t('pages.flow.cta')}
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M4 10h12m0 0l-4-4m4 4l-4 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <p className="mt-3 text-sm font-medium text-[#4a5568]">
          {t('pages.flow.ctaReassurance')}
        </p>
      </section>

      {/* Research */}
      <section className="mx-auto max-w-4xl px-6 py-12">
        <div className="mb-12 flex justify-center">
          <SpeakingMockup />
        </div>

        <div className="grid items-center gap-10 md:grid-cols-2">
          <div className="order-2 flex items-end justify-center gap-3 sm:gap-5 md:order-1">
            {RESEARCH_BARS.map((bar) => (
              <div
                key={bar.labelKey}
                className="flex w-16 flex-col items-center justify-end sm:w-20"
              >
                <span className="mb-1.5 text-sm font-bold text-[#0a1628]">
                  {bar.value}%
                </span>
                <div
                  className={`w-full rounded-t-2xl rounded-b-md ${
                    bar.highlight
                      ? 'from-primary bg-gradient-to-t to-[#5aa9ff]'
                      : 'bg-[#cbd5e1]'
                  }`}
                  style={{ height: `${Math.round((bar.value / 75) * 180)}px` }}
                />
                <span
                  className={`mt-2 text-center text-xs leading-tight ${
                    bar.highlight ? 'text-primary font-bold' : 'text-[#94a3b8]'
                  }`}
                >
                  {t(bar.labelKey)}
                </span>
                {bar.highlight && (
                  <span className="text-primary mt-0.5 flex items-center gap-1 text-center text-xs font-bold">
                    <span className="bg-primary h-2 w-2 rounded-full" />
                    LingoChatAI
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="order-1 text-center md:order-2 md:text-left">
            <h2 className="text-[26px] font-extrabold text-[#0a1628] md:text-[32px]">
              {t('pages.flow.research.heading')}
            </h2>
            <p className="mt-4 text-[17px] leading-relaxed text-[#4a5568]">
              {t('pages.flow.research.text')}
            </p>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="mx-auto max-w-3xl px-6 py-12">
        <h2 className="mb-8 text-center text-[26px] font-extrabold text-[#0a1628] md:text-[32px]">
          {t('pages.flow.comparison.heading')}
        </h2>

        <div className="overflow-hidden rounded-3xl border border-[#e2e8f0] bg-white">
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-[#e2e8f0] px-4 py-3 sm:px-6">
            <span />
            <span className="text-primary w-20 text-center text-xs font-bold sm:text-sm">
              {t('pages.flow.comparison.appName')}
            </span>
            <span className="w-20 text-center text-xs font-semibold text-[#94a3b8] sm:text-sm">
              {t('pages.flow.comparison.others')}
            </span>
          </div>

          {COMPARISON_ROWS.map((rowKey) => (
            <div
              key={rowKey}
              className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-[#f1f5f9] px-4 py-4 last:border-b-0 sm:px-6"
            >
              <span className="text-sm text-[#0a1628] sm:text-base">
                {t(rowKey)}
              </span>
              <span className="flex w-20 justify-center">
                <CheckIcon />
              </span>
              <span className="flex w-20 justify-center">
                <CrossIcon />
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-4xl px-6 py-12">
        <h2 className="mb-12 text-center text-[26px] font-extrabold text-[#0a1628] md:text-[32px]">
          {t('pages.flow.how.heading')}
        </h2>

        <div className="space-y-14">
          {HOW_STEPS.map((step, index) => {
            const { Mockup } = step

            return (
              <div
                key={step.titleKey}
                className="mx-auto flex w-full max-w-lg flex-col items-center text-center"
              >
                <p className="text-primary text-xs font-bold">
                  {t('pages.flow.how.stepLabel', {
                    defaultValue: 'Step {{number}}',
                    number: index + 1,
                  })}
                </p>
                <h3 className="mt-1 text-xl font-bold text-[#0a1628]">
                  {t(step.titleKey)}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[#4a5568]">
                  {t(step.textKey)}
                </p>

                <div className="mt-6 flex w-full justify-center">
                  <Mockup />
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Languages */}
      <section className="mx-auto max-w-3xl px-6 py-12 text-center">
        <h2 className="text-[26px] font-extrabold text-[#0a1628] md:text-[32px]">
          {t('pages.flow.languages.heading')}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-[16px] text-[#4a5568]">
          {t('pages.flow.languages.subheading')}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {FLAG_EMOJIS.map((flag, i) => (
            <span
              key={i}
              className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#e2e8f0] bg-white text-2xl shadow-sm"
            >
              {flag}
            </span>
          ))}
        </div>
      </section>

      {/* Fluent narrative */}
      <section className="mx-auto max-w-3xl px-6 py-12 text-center">
        <h2 className="text-[26px] font-extrabold text-[#0a1628] md:text-[32px]">
          {t('pages.flow.fluent.heading')}
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-[17px] leading-relaxed text-[#4a5568]">
          {t('pages.flow.fluent.text')}
        </p>
      </section>

      {/* Social proof */}
      <section className="mx-auto max-w-3xl space-y-6 px-6 py-12">
        <h2 className="text-center text-[26px] font-extrabold text-[#0a1628] md:text-[32px]">
          {t('pages.flow.social.heading')}
        </h2>
        <SocialProofBanner />
        <SuccessStats />
        <TestimonialCarousel />
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 py-12 text-center">
        <FrequentlyAskedQuestions />
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-3xl px-6 py-14 text-center">
        <h2 className="text-[26px] font-extrabold text-[#0a1628] md:text-[34px]">
          {t('pages.flow.finalCta.heading')}
        </h2>
        <p className="mx-auto mt-3 max-w-lg text-[16px] text-[#4a5568]">
          {t('pages.flow.finalCta.subheading')}
        </p>
        <button
          className="bg-primary shadow-primary/25 hover:shadow-primary/35 mt-8 inline-flex cursor-pointer items-center gap-2 rounded-2xl px-10 py-4 text-lg font-bold text-white uppercase shadow-lg transition-all hover:shadow-xl hover:brightness-110 active:scale-[0.98]"
          onClick={startOnboarding}
        >
          {t('pages.flow.cta')}
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M4 10h12m0 0l-4-4m4 4l-4 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </section>

      <footer className="px-6 py-6 pb-28">
        <div className="flex flex-wrap items-center justify-center gap-x-3 text-base text-[#94a3b8]">
          <Link
            to="https://app-studio.ai/terms"
            className="transition-colors hover:text-[#475569]"
          >
            {t('footer.terms')}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="https://app-studio.ai/privacy"
            className="transition-colors hover:text-[#475569]"
          >
            {t('footer.privacy')}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="/help-center/contact"
            className="transition-colors hover:text-[#475569]"
          >
            {t('footer.contact', { defaultValue: 'Contact' })}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="/help-center/cancel-subscription"
            className="transition-colors hover:text-[#475569]"
          >
            {t('footer.cancelSubscription', {
              defaultValue: 'How to Cancel Subscription',
            })}
          </Link>
        </div>
      </footer>

      {!('ReactNativeWebView' in window) && (
        <div
          className={`fixed inset-x-0 bottom-0 z-50 border-t border-[#e2e8f0] bg-white/90 px-4 py-3 backdrop-blur-sm transition-transform duration-300 ${
            showStickyCta ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <button
            className="bg-primary shadow-primary/25 mx-auto flex w-full max-w-md cursor-pointer items-center justify-center gap-2 rounded-2xl px-8 py-3.5 text-base font-bold text-white uppercase shadow-lg transition-all hover:brightness-110 active:scale-[0.98]"
            onClick={startOnboarding}
          >
            {t('pages.flow.cta')}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M4 10h12m0 0l-4-4m4 4l-4 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}
